import type { CalculatorResult } from "./calculatorData";

export const maxHoldings = 20;
export const maxHoldingValue = 100_000_000;
export const exampleHoldings = [
  { name: "Holding A", value: 5000, target: 40 },
  { name: "Holding B", value: 2500, target: 30 },
  { name: "Holding C", value: 1500, target: 20 },
  { name: "Holding D", value: 1000, target: 10 },
];
export type ConcentrationHolding = { name: string; value: number; target?: number };
export type HoldingDraft = { id: number; name: string; value: string; target: string };
export type ConcentrationScenario = { rows: HoldingDraft[]; compareTargets: boolean };

export function defaultConcentrationScenario(): ConcentrationScenario {
  return { rows: exampleHoldings.map((holding, id) => ({ id, name: holding.name, value: String(holding.value), target: String(holding.target) })), compareTargets: false };
}

const roundCents = (value: number) => Math.round(value * 100) / 100;
export const weightLabel = (value: number) => `${value.toLocaleString("en-US", { maximumFractionDigits: 2 })}%`;
export function driftLabel(value: number) {
  const rounded = roundCents(value) || 0;
  return `${rounded > 0 ? "+" : ""}${rounded.toLocaleString("en-US", { maximumFractionDigits: 2 })} pp`;
}
export const concentrationMoney = (value: number) => value.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function portfolioConcentration(holdings: ConcentrationHolding[]) {
  if (!holdings.length || holdings.length > maxHoldings) throw new RangeError(`Enter 1 to ${maxHoldings} holdings.`);
  const names = new Set<string>();
  for (const holding of holdings) {
    const name = holding.name.trim();
    if (!name || name.length > 40 || names.has(name.toLowerCase())) throw new RangeError("Use distinct holding names up to 40 characters. Combine duplicate positions.");
    names.add(name.toLowerCase());
    if (!Number.isFinite(holding.value) || holding.value < 0 || holding.value > maxHoldingValue) throw new RangeError("Holding values must be finite, non-negative, and within the limit.");
  }
  const total = holdings.reduce((sum, holding) => sum + roundCents(holding.value), 0);
  if (total === 0) throw new RangeError("Enter at least one holding worth $0.01 or more.");
  const targets = holdings.some((holding) => holding.target !== undefined);
  if (targets && holdings.some((holding) => holding.target === undefined || !Number.isFinite(holding.target) || holding.target < 0 || holding.target > 100)) throw new RangeError("Enter a target from 0% to 100% for every holding.");
  const targetTotal = holdings.reduce((sum, holding) => sum + roundCents(holding.target ?? 0), 0);
  if (targets && Math.abs(targetTotal - 100) > 0.000001) throw new RangeError("Target weights must total 100%.");
  const rows = holdings.map((holding) => {
    const value = roundCents(holding.value);
    const weight = value / total * 100;
    const target = targets ? roundCents(holding.target!) : undefined;
    return { name: holding.name.trim(), value, weight, target, drift: target === undefined ? undefined : weight - target };
  });
  const ranked = [...rows].sort((left, right) => right.value - left.value);
  return { total, rows, largest: ranked[0], topThree: ranked.slice(0, 3).reduce((sum, row) => sum + row.weight, 0), fundedCount: rows.filter((row) => row.value > 0).length };
}

export function concentrationResult(analysis: ReturnType<typeof portfolioConcentration>): CalculatorResult {
  return {
    headlineLabel: "Largest entered holding",
    headlineValue: weightLabel(analysis.largest.weight),
    headlineDetail: analysis.largest.name,
    stats: [
      { label: "Entered portfolio value", value: concentrationMoney(analysis.total) },
      { label: "Top-three weight", value: weightLabel(analysis.topThree), detail: "Uses all funded holdings when fewer than three are entered." },
      { label: "Funded holdings", value: String(analysis.fundedCount) },
    ],
    chart: { kind: "breakdown", valueLabel: "Entered holding values", segments: analysis.rows.map((row) => ({ label: row.name, value: row.value, tone: "accent" })) },
    note: "Direct positions only, not ETF look-through or a risk score",
  };
}

export function calculatePortfolioConcentration(values: Record<string, number>): CalculatorResult {
  return concentrationResult(portfolioConcentration(exampleHoldings.map((holding, index) => ({ name: holding.name, value: values[`holding${index + 1}`] }))));
}

export function parseHoldingNumber(raw: string, max: number) {
  if (raw.trim() === "") return null;
  const value = Number(raw);
  return Number.isFinite(value) && value >= 0 && value <= max ? roundCents(value) : null;
}

export function validateConcentrationDraft(scenario: ConcentrationScenario) {
  const names = scenario.rows.map((row) => row.name.trim().toLowerCase());
  const errors = scenario.rows.map((row) => ({
    name: !row.name.trim() || row.name.trim().length > 40 ? "Enter a holding name, up to 40 characters." : names.filter((name) => name === row.name.trim().toLowerCase()).length > 1 ? "Combine duplicate holdings into one row." : null,
    value: parseHoldingNumber(row.value, maxHoldingValue) === null ? "Enter a value from 0 to 100,000,000." : null,
    target: scenario.compareTargets && parseHoldingNumber(row.target, 100) === null ? "Enter a target from 0% to 100%." : null,
  }));
  const targetTotal = scenario.rows.reduce((sum, row) => sum + (parseHoldingNumber(row.target, 100) ?? 0), 0);
  const targetError = scenario.compareTargets && (errors.some((error) => error.target) || Math.abs(targetTotal - 100) > 0.000001) ? `Targets total ${weightLabel(targetTotal)}. Enter every target and make the total 100%.` : null;
  if (errors.some((error) => error.name || error.value)) return { errors, targetError, analysis: null, message: "Complete the highlighted holding fields to see results." };
  const holdings = scenario.rows.map((row) => ({ name: row.name, value: parseHoldingNumber(row.value, maxHoldingValue)!, target: scenario.compareTargets && !targetError ? parseHoldingNumber(row.target, 100)! : undefined }));
  try {
    return { errors, targetError, analysis: portfolioConcentration(holdings), message: null };
  } catch (error) {
    return { errors, targetError, analysis: null, message: error instanceof RangeError ? error.message : "Check the holding values." };
  }
}

export function concentrationScenarioUrl(scenario: ConcentrationScenario, pageUrl: string) {
  const validation = validateConcentrationDraft(scenario);
  if (!validation.analysis || validation.targetError) throw new RangeError("Complete the scenario before sharing.");
  const url = new URL(pageUrl);
  const holdings = scenario.rows.map((row) => ({ name: row.name.trim(), value: parseHoldingNumber(row.value, maxHoldingValue), ...(scenario.compareTargets ? { target: parseHoldingNumber(row.target, 100) } : {}) }));
  url.search = "";
  url.hash = `scenario:${new URLSearchParams({ holdings: JSON.stringify(holdings) })}`;
  return url.toString();
}

export function readConcentrationScenario(hash: string): { scenario: ConcentrationScenario; notice: string | null } {
  const fallback = defaultConcentrationScenario();
  if (!hash.startsWith("#scenario:")) return { scenario: fallback, notice: null };
  try {
    if (hash.length > 16_000) throw new RangeError();
    const raw: unknown = JSON.parse(new URLSearchParams(hash.slice(10)).get("holdings") ?? "null");
    if (!Array.isArray(raw) || !raw.length || raw.length > maxHoldings) throw new RangeError();
    const rows: HoldingDraft[] = raw.map((item: unknown, id) => {
      if (!item || typeof item !== "object" || !("name" in item) || typeof item.name !== "string" || !("value" in item) || typeof item.value !== "number") throw new RangeError();
      if ("target" in item && typeof item.target !== "number") throw new RangeError();
      return { id, name: item.name, value: String(item.value), target: "target" in item ? String(item.target) : "" };
    });
    const scenario = { rows, compareTargets: rows.some((row) => row.target !== "") };
    const validation = validateConcentrationDraft(scenario);
    if (!validation.analysis || validation.targetError) throw new RangeError();
    return { scenario, notice: null };
  } catch {
    return { scenario: fallback, notice: "This shared scenario is invalid. The example is shown instead." };
  }
}
